import { cn } from "@/lib/cn";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { type ComponentPropsWithoutRef, type ReactNode, forwardRef } from "react";

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-[var(--iris-r-sm)] border border-[var(--iris-line-2)] bg-[var(--iris-bg-3)] px-2 py-1 text-xs text-[var(--iris-fg-2)] backdrop-blur-xl",
        "shadow-[var(--iris-shadow-raised)]",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * The one hover delay for the whole app. App.tsx sets it at the root and
 * `StandaloneTooltip` repeats it, so a tooltip behaves the same either way.
 */
export const TOOLTIP_DELAY_MS = 100;

/**
 * IR-1433 r4 — a Tooltip that brings its own provider.
 *
 * `Tooltip` THROWS when no `TooltipProvider` sits above it, and that throw
 * error-boundaries the entire page — the IR-1247 failure exactly. App.tsx
 * provides one at the root, so the running app is safe; what is not safe is the
 * assumption. Putting a tooltip inside a table cell in `avatars-tab` turned 27
 * test files and 216 tests red in one commit, because every one of those
 * harnesses mounts the tab the way a future embedder might: on its own.
 *
 * A component that works only because something far away remembered to wrap it
 * is a fuse, not a guarantee. Nesting a provider is supported by Radix (it just
 * re-supplies context to its subtree), costs nothing, and makes the cell render
 * identically whether or not the root provider is there.
 *
 * Use this for a tooltip that lives inside a reusable surface. The bare
 * `Tooltip` stays correct for anything rendered under a provider you can see.
 */
export function StandaloneTooltip({
  children,
  ...props
}: ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & { children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>
    </TooltipProvider>
  );
}
